import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from '@helpcenter/analytics-components/src/link';
import type { State } from '@helpcenter/common-state-selectors';
import { getThemeColor } from '@helpcenter/common-state-selectors/src/help-center';
import { sanitize as isomorphicSanitize } from 'isomorphic-dompurify';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { usePathParam } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import ShortcutIcon from '@atlaskit/icon/core/shortcut';
import { Box } from '@atlaskit/primitives/box';
import { Inline } from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { Text } from '@atlaskit/primitives/text';
import { urlRefsAndTarget } from '@atlassian/help-center-common-util/is-external-resource';
import articleMessages from '../articles/messages';
import { formatText, sendSearchUIAnalyticEvents, useVisibilityState } from '../common';
import { ResourceIcon } from '../resource-icon';
import type { requestFormItemFragmentV2$key } from './__generated__/requestFormItemFragmentV2.graphql';

export interface Props {
	itemOrder: number;
	term: string;
	themeColor: string;
	result: requestFormItemFragmentV2$key;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
}

export const RequestFormItem = ({
	itemOrder,
	result,
	term,
	themeColor,
	createAnalyticsEvent,
}: Props) => {
	const { formatMessage } = useIntl();
	const data = useFragment<requestFormItemFragmentV2$key>(
		graphql`
			fragment requestFormItemFragmentV2 on HelpObjectStoreRequestTypeSearchResult {
				id
				title
				displayLink
				metadata {
					isExternal
					searchStrategy
				}
			}
		`,
		result,
	);
	const [portalId] = usePathParam('portalId');
	const { isShortcutIconVisible, setIsHovered, setIsFocused } = useVisibilityState();
	const { title, displayLink, id, metadata } = data;
	const { isExternal, searchStrategy } = metadata;
	const shortcutIcon = isExternal ? (
		<ShortcutIcon
			label={formatMessage(articleMessages.shortcutIcon)}
			size="medium"
			spacing="compact"
		/>
	) : null;
	const highlightedTitle = title ? formatText(title, term) : '';
	const linkProps = urlRefsAndTarget({ displayLink, isAnExternalResource: isExternal });

	useEffect(() => {
		sendSearchUIAnalyticEvents(
			{
				id,
				portalId,
				searchAlgorithm: searchStrategy,
				action: 'list-item-rendered',
				resourceType: 'request-form',
			},
			createAnalyticsEvent,
			portalId,
		);
	}, [createAnalyticsEvent, id, portalId, searchStrategy]);

	return (
		<Box
			xcss={containerStyles}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocusCapture={() => setIsFocused(true)}
			onBlurCapture={() => setIsFocused(false)}
		>
			<Inline xcss={[headingStyles, headingAlignmentStyles]} space="space.100">
				<ResourceIcon entityType="REQUEST_FORM" displayLink={displayLink} />
				<Link
					key={id}
					actionSubjectId={isExternal ? 'external-request-form-link' : 'request-form-link'}
					data-test-id="result-link-container"
					aria-label={title}
					attributes={{
						id,
						itemOrder,
						searchAlgorithm: searchStrategy,
						isEventFromPortal: !!portalId,
					}}
					{...linkProps}
				>
					<Text as="p">
						<span
							style={{
								color: themeColor,
							}}
							dangerouslySetInnerHTML={{
								__html: isomorphicSanitize(highlightedTitle),
							}}
						/>
					</Text>
				</Link>
				<Box
					xcss={
						isShortcutIconVisible ? headerRightAreaStylesVisible : headerRightAreaStylesInVisible
					}
					testId="search-result-shortcut-icon"
				>
					{shortcutIcon}
				</Box>
			</Inline>
		</Box>
	);
};

const ConnectedRequestFormItem = connect((state: State) => {
	return {
		themeColor: getThemeColor(state),
	};
})(RequestFormItem);

export default withAnalyticsEvents()(ConnectedRequestFormItem);

const containerStyles = xcss({
	overflow: 'hidden',
	width: '100%',
	padding: 'space.100',
	gap: 'space.150',
	borderRadius: 'radius.small',
});

const headingStyles = xcss({
	overflow: 'hidden',
	width: '100%',
});

const headingAlignmentStyles = xcss({
	alignItems: 'center',
});

const headerRightAreaStylesInVisible = xcss({
	display: 'flex',
	opacity: 0,
	alignItems: 'flex-start',
	justifyContent: 'flex-end',
});
const headerRightAreaStylesVisible = xcss({
	display: 'flex',
	opacity: 1,
	alignItems: 'flex-start',
});
